// Liste Detayı (görev #222 + kopyaları #225 #228 #231 #234 #237 #240 #243 #246; Vegas #221, Hera #223…,
// sahip asya 2026-10-14): her listenin GÜNLÜK / HAFTALIK / AYLIK istatistiği — çağrı, cevaplama, görüşme,
// başarı (yatıran ÷ görüşme), yatırım, SMS — ve SLACK / FORM / TAKİP'te aynı ölçüler ARAMA SEBEBİNE göre.
//
// Veri GET /api/list-detail: çağrı/görüşme/yatırım/sebep haftalık raporun sheet taramasından (Haftalık Rapor'un
// Liste Kırılımı ile AYNI tanımlar ve AYNI cache), SMS paneldeki sms_events'ten (çağrıya bağlanan). Eksik değer
// BOŞ kalır (§6): okunamayan hafta 0 değildir, paydası 0 olan oran basılmaz.

import { useEffect, useMemo, useState } from 'react';
import { ApiError, getListDetail, getStatusSystems } from '../api';
import { brandLabel, WEEKLY_REPORT_BRANDS } from '../constants';
import { downloadCSV, fmtDate, fmtMoney, fmtPct1, fmtRange, todayInTz } from '../format';
import { CsvButton, dateInputStyle, dotStyle, EmptyState, segStyle, Skeleton } from '../ui';
import type { BrandId, ListDetailData, ListDetailList, ListDetailPeriod, ListDetailReason, ListDetailStats, StatusKey } from '../types';

const PERIODS: { key: ListDetailPeriod; label: string }[] = [
  { key: 'day', label: 'Günlük' },
  { key: 'week', label: 'Haftalık' },
  { key: 'month', label: 'Aylık' },
];
const WEEKDAY_TR = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];
const REASON_ALL = '__all__';
// Haftalık Rapor'un sonuç sütunları: miras dört statü + tanımsız hariç (WeeklyReport.tsx RESULTS ile aynı süzgeç).
const HIDDEN_RESULTS = new Set<StatusKey>(['answered', 'unreachable', 'busy', 'dissatisfied', 'other']);
const RESULTS = getStatusSystems().tr.filter((st) => !HIDDEN_RESULTS.has(st.key));
const AUTO_RETRY_MS = 15_000;
const AUTO_RETRY_MAX = 4;

const th: React.CSSProperties = {
  fontSize: '10px',
  color: 'var(--faint)',
  letterSpacing: '.05em',
  fontWeight: 600,
  padding: '8px 10px',
  textAlign: 'right',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid var(--border)',
};
const td: React.CSSProperties = {
  fontSize: '12.5px',
  color: 'var(--text)',
  padding: '8px 10px',
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid var(--border)',
};
const card: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: '14px',
  padding: '16px 18px',
  marginBottom: '16px',
  minWidth: 0,
};
const PENDING_COLOR = '#e0a23c';

/** Oran (tek ondalık) — payda 0 ise null → BOŞ (§6; %0,0 bir SONUÇTUR, ölçüm yokluğu değil). */
const rate = (n: number, d: number): number | null => (d > 0 ? Math.round((1000 * n) / d) / 10 : null);

const dayLabel = (day: string) => WEEKDAY_TR[new Date(day + 'T12:00:00Z').getUTCDay()];

/** Bir dilimin standart sütunları — Liste satırı, gün satırı, sebep satırı aynı sırayı kullanır. */
const STAT_HEADERS = ['ÇAĞRI', 'YANITLANAN', 'CEVAPLAMA', 'GÖRÜŞME', 'BAŞARI 24S', 'BAŞARI 48S', 'YATIRIM 24S', 'YATIRIM 48S'];
const STAT_TITLES: Record<string, string> = {
  ÇAĞRI: 'Arama Yapılmadı hariç',
  CEVAPLAMA: 'Yanıtlanan / çağrı',
  'BAŞARI 24S': 'Yatırım yapan / görüşme',
  'BAŞARI 48S': 'Yatırım yapan / görüşme',
  'SMS ORANI': 'SMS / çağrı',
};

function statValues(s: ListDetailStats, tracked: boolean): string[] {
  return [
    String(s.calls),
    String(s.answered),
    fmtPct1(rate(s.answered, s.calls)),
    String(s.conversations),
    tracked ? fmtPct1(rate(s.success12h, s.conversations)) : '',
    tracked ? fmtPct1(rate(s.success36h, s.conversations)) : '',
    tracked ? fmtMoney(s.deposit12h) : '',
    tracked ? fmtMoney(s.deposit36h) : '',
  ];
}

function smsValues(s: ListDetailStats, smsOk: boolean): string[] {
  return smsOk ? [String(s.sms), fmtPct1(rate(s.sms, s.calls))] : ['', ''];
}

/** Bir dilimi hücrelere çevirir; para hücresi, o pencerenin hücreleri henüz boşsa amber (rakam artabilir). */
function StatCells({ s, tracked, bold, withSms, smsOk }: {
  s: ListDetailStats | null;
  tracked: boolean;
  bold?: boolean;
  withSms?: boolean;
  smsOk?: boolean;
}) {
  const n = STAT_HEADERS.length + (withSms ? 2 : 0);
  if (!s) return <>{Array.from({ length: n }, (_, i) => <td key={i} style={td} />)}</>;
  const vals = statValues(s, tracked).concat(withSms ? smsValues(s, !!smsOk) : []);
  return (
    <>
      {vals.map((v, i) => {
        const pending = tracked && ((i === 6 && s.pending12h > 0) || (i === 7 && s.pending36h > 0));
        const muted = i === 2 || i === 4 || i === 5 || i === 9;
        return (
          <td
            key={i}
            style={{
              ...td,
              fontWeight: bold ? 700 : 400,
              color: pending ? PENDING_COLOR : muted && !bold ? 'var(--dim)' : 'var(--text)',
            }}
          >
            {v}
          </td>
        );
      })}
    </>
  );
}

function HeaderRow({ first, withSms, extra }: { first: string; withSms?: boolean; extra?: string }) {
  const cols = (extra ? [extra] : []).concat(STAT_HEADERS, withSms ? ['SMS', 'SMS ORANI'] : []);
  return (
    <tr>
      <th style={{ ...th, textAlign: 'left' }}>{first}</th>
      {cols.map((h) => (
        <th key={h} style={th} title={STAT_TITLES[h]}>
          {h}
        </th>
      ))}
    </tr>
  );
}

function CardHead({ title, tag, onCsv }: { title: string; tag?: string; onCsv?: () => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
      {tag && <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--accent)' }}>{tag}</span>}
      <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>{title}</span>
      {onCsv && (
        <span style={{ marginLeft: 'auto' }}>
          <CsvButton small onClick={onCsv} />
        </span>
      )}
    </div>
  );
}

const csvStats = (s: ListDetailStats | null, tracked: boolean, sms?: boolean, smsOk?: boolean) =>
  s ? statValues(s, tracked).concat(sms ? smsValues(s, !!smsOk) : []) : [];

/** İki (ya da daha çok) sebep dilimini topla — "Üçü birden" görünümü. */
function mergeReasons(lists: ListDetailList[]) {
  const acc = new Map<string, ListDetailReason>();
  for (const l of lists) {
    for (const r of l.reasons) {
      const cur = acc.get(r.reason) || { ...r, rows: 0, calls: 0, answered: 0, conversations: 0, success12h: 0, success36h: 0, deposit12h: 0, deposit36h: 0, pending12h: 0, pending36h: 0, sms: 0, results: {} };
      for (const k of ['rows', 'calls', 'answered', 'conversations', 'success12h', 'success36h', 'deposit12h', 'deposit36h', 'pending12h', 'pending36h'] as const) {
        cur[k] = Math.round((cur[k] + r[k]) * 100) / 100;
      }
      cur.results = sumResults([cur, r]);
      acc.set(r.reason, cur);
    }
  }
  return [...acc.values()].sort((a, b) => b.rows - a.rows || a.reason.localeCompare(b.reason, 'tr'));
}

/** Sebeplerin sonuç dağılımlarını topla (yeni nesne — kaynak satırlar değişmez). */
function sumResults(rows: { results?: Partial<Record<StatusKey, number>> }[]): Partial<Record<StatusKey, number>> {
  const out: Partial<Record<StatusKey, number>> = {};
  for (const r of rows) {
    for (const [k, n] of Object.entries(r.results || {}) as [StatusKey, number][]) out[k] = (out[k] || 0) + n;
  }
  return out;
}

/** Sebep × sonuç (#276): her sebepte kaç satır hangi sonuçla kapandı. Sütunlar "Sonuç dağılımı" kartıyla aynı; 0 da bir değerdir (soluk basılır). */
function ReasonResultsTable({ rows }: { rows: ListDetailReason[] }) {
  const total = sumResults(rows);
  const totalRows = rows.reduce((n, r) => n + r.rows, 0);
  const cells = (results: Partial<Record<StatusKey, number>>, base: number, bold?: boolean) =>
    RESULTS.map((st) => {
      const n = results[st.key] || 0;
      return (
        <td
          key={st.key}
          title={n ? fmtPct1(rate(n, base)) : undefined}
          style={{ ...td, fontWeight: bold ? 700 : n ? 600 : 400, color: n ? 'var(--text)' : 'var(--faint)', borderBottom: bold ? 'none' : td.borderBottom }}
        >
          {n}
        </td>
      );
    });
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ ...th, textAlign: 'left', verticalAlign: 'bottom' }}>SEBEP</th>
            <th style={{ ...th, verticalAlign: 'bottom' }}>TALEP</th>
            {RESULTS.map((st) => (
              <th key={st.key} style={{ ...th, whiteSpace: 'normal', minWidth: '74px', maxWidth: '112px', lineHeight: 1.3, verticalAlign: 'bottom' }}>
                <span style={{ ...dotStyle(st.color), display: 'inline-block', marginRight: '5px' }} />
                <span>{st.label}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.reason}>
              <td style={{ ...td, textAlign: 'left', fontWeight: 600, color: 'var(--dim)' }}>{r.reason}</td>
              <td style={{ ...td, color: 'var(--dim)' }}>{r.rows}</td>
              {cells(r.results || {}, r.rows)}
            </tr>
          ))}
          <tr>
            <td style={{ ...td, textAlign: 'left', fontWeight: 700, borderBottom: 'none' }}>Toplam</td>
            <td style={{ ...td, fontWeight: 700, borderBottom: 'none' }}>{totalRows}</td>
            {cells(total, totalRows, true)}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function sumStats(rows: ListDetailStats[]): ListDetailStats {
  const out: ListDetailStats = { rows: 0, calls: 0, answered: 0, conversations: 0, success12h: 0, success36h: 0, deposit12h: 0, deposit36h: 0, pending12h: 0, pending36h: 0, sms: 0 };
  for (const r of rows) {
    for (const k of Object.keys(out) as (keyof ListDetailStats)[]) out[k] = Math.round((out[k] + r[k]) * 100) / 100;
  }
  return out;
}

export default function ListDetail({ brands }: { brands: BrandId[] }) {
  const reportBrands = brands.filter((b) => WEEKLY_REPORT_BRANDS.includes(b));
  const [brandPick, setBrandPick] = useState<BrandId | null>(null);
  const brand = brandPick && reportBrands.includes(brandPick) ? brandPick : reportBrands[0] || null;
  const today = todayInTz('Europe/Istanbul');
  const [period, setPeriod] = useState<ListDetailPeriod>('week');
  const [date, setDate] = useState<string>(today);
  const [data, setData] = useState<ListDetailData | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'busy' | 'error'>('loading');
  const [tick, setTick] = useState(0);
  const [autoTries, setAutoTries] = useState(0);
  const [listKey, setListKey] = useState('slack');
  const [reasonTab, setReasonTab] = useState<string>(REASON_ALL);

  useEffect(() => setAutoTries(0), [brand, period, date]);

  useEffect(() => {
    if (!brand) return;
    let alive = true;
    setState('loading');
    getListDetail(brand, period, date).then(
      (d) => {
        if (!alive) return;
        setData(d);
        setState('ok');
      },
      (e: unknown) => {
        if (!alive) return;
        setData(null);
        setState(e instanceof ApiError && e.status === 429 ? 'busy' : 'error');
      },
    );
    return () => {
      alive = false;
    };
  }, [brand, period, date, tick]);

  // Soğuk haftalar hesaplanırken (kapı dolu) o haftanın günleri boş gelir — kendiliğinden birkaç kez yeniden sor.
  const waiting = !!data && !data.complete && data.weeks.some((w) => w.status === 'busy');
  useEffect(() => {
    if (!waiting || autoTries >= AUTO_RETRY_MAX) return;
    const t = setTimeout(() => {
      setAutoTries((n) => n + 1);
      setTick((n) => n + 1);
    }, AUTO_RETRY_MS);
    return () => clearTimeout(t);
  }, [waiting, autoTries, data]);

  const selected = data ? data.lists.find((l) => l.key === listKey) || data.lists[0] : null;
  const reasonLists = useMemo(() => (data ? data.lists.filter((l) => l.hasReasons) : []), [data]);
  const reasonRows = useMemo(() => {
    if (!reasonLists.length) return [];
    if (reasonTab === REASON_ALL) return mergeReasons(reasonLists);
    return (reasonLists.find((l) => l.key === reasonTab) || reasonLists[0]).reasons;
  }, [reasonLists, reasonTab]);
  const smsOk = !!data && data.sms.available;
  const dayRows = selected ? selected.days.filter((d) => period === 'week' || !d.future) : [];
  const rangeLabel = data ? (data.from === data.to ? fmtDate(data.from) : fmtRange(fmtDate(data.from), fmtDate(data.to))) : '';
  const fileBase = data ? `liste-detayi-${data.brand}-${data.from}_${data.to}` : 'liste-detayi';

  if (!brand) {
    return <EmptyState icon="filter_alt" title="Marka seçilmedi" reason="Üstteki marka filtresinden en az bir marka seçin." />;
  }

  const notes: string[] = [];
  if (data && !data.complete) {
    notes.push(waiting && autoTries < AUTO_RETRY_MAX
      ? 'Bazı haftalar şu an hesaplanıyor — boş günler birazdan kendiliğinden dolar.'
      : 'Bu dönemin bir kısmı okunamadı — boş günler bilinmiyor (0 değil).');
  }
  if (data && !data.settled) notes.push('Yatırım tutarları henüz kesinleşmedi (48 saatlik pencereler açık) — rakamlar değişebilir.');
  if (data && !smsOk) notes.push('SMS verisi okunamadı — SMS sütunları boş.');

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
        {reportBrands.length > 1 &&
          reportBrands.map((b) => (
            <button key={b} onClick={() => setBrandPick(b)} style={segStyle(b === brand)}>
              {brandLabel(b)}
            </button>
          ))}
        {reportBrands.length > 1 && <span style={{ width: '6px' }} />}
        {PERIODS.map((p) => (
          <button key={p.key} onClick={() => setPeriod(p.key)} style={segStyle(p.key === period)}>
            {p.label}
          </button>
        ))}
        <input
          type="date"
          value={date}
          max={today}
          onChange={(e) => e.target.value && setDate(e.target.value)}
          style={dateInputStyle}
        />
        {data && <span style={{ fontSize: '12px', color: 'var(--faint)' }}>{rangeLabel}</span>}
        <span style={{ marginLeft: 'auto', fontSize: '11px', color: 'var(--dim)', display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
          <span>Çağrı · görüşme · yatırım: haftalık rapor</span>
          <span>SMS: panel (çağrıya bağlanan)</span>
        </span>
      </div>

      {state === 'loading' && !data && <Skeleton heights={[260, 220, 180]} />}

      {(state === 'busy' || state === 'error') && !data && (
        <div style={{ ...card, display: 'flex', alignItems: 'center', gap: '12px', fontSize: '12.5px', color: 'var(--dim)' }}>
          <span>{state === 'busy' ? 'Haftalık rapor şu an başka bir hafta için hesaplanıyor.' : 'Liste verisi alınamadı.'}</span>
          <button onClick={() => setTick((n) => n + 1)} style={{ ...segStyle(false), padding: '5px 10px', fontSize: '11.5px' }}>
            Yenile
          </button>
        </div>
      )}

      {data && (
        <div style={{ opacity: state === 'loading' ? 0.6 : 1, transition: 'opacity .15s' }}>
          {(notes.length > 0 || data.caveats.length > 0) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '12px', fontSize: '11.5px', color: 'var(--dim)' }}>
              {notes.map((n) => (
                <span key={n}>{n}</span>
              ))}
              {data.caveats.map((c) => (
                <span key={c} style={{ color: PENDING_COLOR }}>
                  {c}
                </span>
              ))}
              {!data.complete && (
                <span>
                  <button onClick={() => setTick((n) => n + 1)} style={{ ...segStyle(false), padding: '4px 9px', fontSize: '11px' }}>
                    Yenile
                  </button>
                </span>
              )}
            </div>
          )}

          {/* 1 · Bütün listeler, seçili dönem — satıra tıkla → aşağıdaki kartlar o listeyi gösterir. */}
          <div style={card}>
            <CardHead
              title="Listeler"
              onCsv={() =>
                downloadCSV(fileBase + '.csv', [
                  ['LİSTE', ...STAT_HEADERS, 'SMS', 'SMS ORANI'],
                  ...data.lists.map((l) => [l.tab, ...csvStats(l.totals, l.depositTracked, true, smsOk)]),
                  ['Toplam', ...csvStats(data.total, true, true, smsOk)],
                ])
              }
            />
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <HeaderRow first="LİSTE" withSms />
                </thead>
                <tbody>
                  {data.lists.map((l) => {
                    const isSel = selected?.key === l.key;
                    return (
                      <tr
                        key={l.key}
                        onClick={() => setListKey(l.key)}
                        title={l.missing ? 'Sekme bulunamadı' : undefined}
                        style={{ cursor: 'pointer', background: isSel ? 'var(--accent-soft)' : 'transparent', opacity: l.missing ? 0.55 : 1 }}
                      >
                        <td style={{ ...td, textAlign: 'left', fontWeight: 600, color: isSel ? 'var(--text)' : 'var(--dim)' }}>{l.tab}</td>
                        <StatCells s={l.totals} tracked={l.depositTracked} withSms smsOk={smsOk} />
                      </tr>
                    );
                  })}
                  <tr>
                    <td style={{ ...td, textAlign: 'left', fontWeight: 700, borderBottom: 'none' }}>Toplam</td>
                    <StatCells s={data.total} tracked bold withSms smsOk={smsOk} />
                  </tr>
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '10px', fontSize: '11px', color: 'var(--faint)' }}>
              <span>Amber tutar: bazı yatırım hücreleri henüz boş — rakam artabilir.</span>
              {smsOk && (data.sms.unattributed || 0) > 0 && (
                <span>
                  <span>Listeye bağlanamayan SMS</span> <span>{data.sms.unattributed}</span>
                </span>
              )}
            </div>
          </div>

          {selected && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0 16px', alignItems: 'flex-start' }}>
              {/* 2 · Seçili liste, gün gün (Günlük dönemde tek gün = yukarıdaki satır; yine de basılır). Dar ekranda alta sarar. */}
              <div style={{ ...card, flex: '999 1 620px' }}>
                <CardHead
                  tag={selected.tab}
                  title="Günlere göre"
                  onCsv={() =>
                    downloadCSV(fileBase + '-' + selected.key + '-gunler.csv', [
                      ['GÜN', ...STAT_HEADERS, 'SMS', 'SMS ORANI'],
                      ...dayRows.map((d) => [d.day, ...csvStats(d.stats, selected.depositTracked, true, smsOk)]),
                      ['Toplam', ...csvStats(selected.totals, selected.depositTracked, true, smsOk)],
                    ])
                  }
                />
                <div style={{ overflowX: 'auto', maxHeight: period === 'month' ? '560px' : undefined, overflowY: period === 'month' ? 'auto' : undefined }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <HeaderRow first="GÜN" withSms />
                    </thead>
                    <tbody>
                      {dayRows.map((d) => (
                        <tr
                          key={d.day}
                          title={!d.future && !d.stats ? 'Bu günün haftası henüz okunamadı' : undefined}
                          style={{ background: d.day === date ? 'var(--accent-soft)' : 'transparent', opacity: d.future ? 0.45 : 1 }}
                        >
                          <td style={{ ...td, textAlign: 'left', color: 'var(--dim)' }}>
                            <span style={{ fontWeight: 600 }}>{dayLabel(d.day)}</span>
                            <span style={{ marginLeft: '8px', color: 'var(--faint)' }}>{fmtDate(d.day).slice(0, 5)}</span>
                          </td>
                          {d.stats ? (
                            <StatCells s={d.stats} tracked={selected.depositTracked} withSms smsOk={smsOk} />
                          ) : (
                            <>
                              <StatCells s={null} tracked={false} />
                              <td style={{ ...td, color: 'var(--dim)' }}>{smsOk && d.sms != null && !d.future ? d.sms : ''}</td>
                              <td style={td} />
                            </>
                          )}
                        </tr>
                      ))}
                      <tr>
                        <td style={{ ...td, textAlign: 'left', fontWeight: 700, borderBottom: 'none' }}>Toplam</td>
                        <StatCells s={selected.totals} tracked={selected.depositTracked} bold withSms smsOk={smsOk} />
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 3 · Seçili listenin sonuç dağılımı (satır = durumu girilmiş her satır, Arama Yapılmadı dahil). */}
              <div style={{ ...card, flex: '1 1 300px' }}>
                <CardHead tag={selected.tab} title="Sonuç dağılımı" />
                {selected.totals.rows === 0 ? (
                  <div style={{ fontSize: '12px', color: 'var(--dim)', padding: '6px 0' }}>Bu dönemde çağrı yok.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {RESULTS.map((st) => {
                      const n = selected.results[st.key] || 0;
                      const share = rate(n, selected.totals.rows);
                      return (
                        <div key={st.key}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
                            <span style={dotStyle(st.color)} />
                            <span style={{ flex: 1, color: n ? 'var(--text)' : 'var(--faint)' }}>{st.label}</span>
                            <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: n ? 'var(--text)' : 'var(--faint)' }}>{n}</span>
                            <span style={{ width: '48px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--dim)' }}>
                              {fmtPct1(share)}
                            </span>
                          </div>
                          <div style={{ height: '4px', borderRadius: '2px', background: 'var(--border)', marginTop: '4px' }}>
                            <div style={{ width: (share || 0) + '%', height: '100%', borderRadius: '2px', background: st.color }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 4 · SLACK / FORM / TAKİP — arama sebebine göre (sahibin listesi: sheet'in H sütunu, BİREBİR metin). */}
          {reasonLists.length > 0 && (
            <div style={card}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
                <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', marginRight: '6px' }}>Arama sebebine göre</span>
                {reasonLists.map((l) => (
                  <button key={l.key} onClick={() => setReasonTab(l.key)} style={{ ...segStyle(reasonTab === l.key), padding: '5px 10px', fontSize: '11.5px' }}>
                    {l.tab}
                  </button>
                ))}
                <button onClick={() => setReasonTab(REASON_ALL)} style={{ ...segStyle(reasonTab === REASON_ALL), padding: '5px 10px', fontSize: '11.5px' }}>
                  Üçü birden
                </button>
                <span style={{ marginLeft: 'auto' }}>
                  <CsvButton
                    small
                    onClick={() =>
                      downloadCSV(fileBase + '-sebepler.csv', [
                        ['SEBEP', 'TALEP', ...STAT_HEADERS],
                        ...reasonRows.map((r) => [r.reason, r.rows, ...csvStats(r, true)]),
                        ['Toplam', sumStats(reasonRows).rows, ...csvStats(sumStats(reasonRows), true)],
                      ])
                    }
                  />
                </span>
              </div>
              {reasonRows.length === 0 ? (
                <div style={{ fontSize: '12px', color: 'var(--dim)', padding: '6px 0' }}>Bu dönemde sebep girilmiş çağrı yok.</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <HeaderRow first="SEBEP" extra="TALEP" />
                    </thead>
                    <tbody>
                      {reasonRows.map((r) => (
                        <tr key={r.reason}>
                          <td style={{ ...td, textAlign: 'left', fontWeight: 600, color: 'var(--dim)' }}>{r.reason}</td>
                          <td style={{ ...td, color: 'var(--dim)' }}>{r.rows}</td>
                          <StatCells s={r} tracked />
                        </tr>
                      ))}
                      <tr>
                        <td style={{ ...td, textAlign: 'left', fontWeight: 700, borderBottom: 'none' }}>Toplam</td>
                        <td style={{ ...td, fontWeight: 700, borderBottom: 'none' }}>{sumStats(reasonRows).rows}</td>
                        <StatCells s={sumStats(reasonRows)} tracked bold />
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
              {reasonRows.length > 0 && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '20px 0 6px' }}>
                    <span style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--text)' }}>Sebebe göre sonuçlar</span>
                    <span style={{ marginLeft: 'auto' }}>
                      <CsvButton
                        small
                        onClick={() => {
                          const total = sumResults(reasonRows);
                          downloadCSV(fileBase + '-sebep-sonuclari.csv', [
                            ['SEBEP', 'TALEP', ...RESULTS.map((st) => st.label)],
                            ...reasonRows.map((r) => [r.reason, r.rows, ...RESULTS.map((st) => (r.results || {})[st.key] || 0)]),
                            ['Toplam', sumStats(reasonRows).rows, ...RESULTS.map((st) => total[st.key] || 0)],
                          ]);
                        }}
                      />
                    </span>
                  </div>
                  <ReasonResultsTable rows={reasonRows} />
                </>
              )}
              <div style={{ marginTop: '10px', fontSize: '11px', color: 'var(--faint)' }}>
                TALEP: o sebeple girilmiş her satır (Arama Yapılmadı dahil) · ÇAĞRI: yapılan arama
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
